/** Field-agent home page — read-only list of clients assigned to the field agent.
 * Tapping a row prompts the PHI warning (per session per client) before showing details. */
import { useEffect, useState } from "react";
import api from "@/api/client";
import Header from "@/components/Header";
import BrandFooter from "@/components/BrandFooter";
import PageHeaderBand from "@/components/PageHeaderBand";
import EmptyState from "@/components/EmptyState";
import PhiWarningModal from "@/components/PhiWarningModal";
import IdleLogout from "@/components/IdleLogout";
import { ShieldCheck, Users, Phone, Calendar as Cal, MapPin } from "lucide-react";
import { stageColor } from "@/lib/palette";
import { deriveInitials } from "@/lib/pipeline";
import { useAuth } from "@/context/AuthContext";

const SESSION_KEY = "avida_phi_ack";

function getAckSet() {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(SESSION_KEY) || "[]"));
  } catch { return new Set(); }
}
function saveAckSet(s) {
  sessionStorage.setItem(SESSION_KEY, JSON.stringify([...s]));
}

export default function FieldAgentHome() {
  const { user } = useAuth();
  const [cards, setCards] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);   // card to open after PHI ack
  const [selectedName, setSelectedName] = useState(""); // for modal text
  const [selectedCard, setSelectedCard] = useState(null); // resolved + viewing
  const [acked, setAcked] = useState(() => getAckSet());

  useEffect(() => {
    Promise.all([api.get("/cards"), api.get("/templates")]).then(([c, t]) => {
      setCards(c.data);
      setTemplates(t.data);
    });
  }, []);

  const tplByStage = Object.fromEntries(templates.map((t) => [t.stage_number, t]));

  const askToOpen = (c) => {
    if (acked.has(c.id)) {
      // already acked this session — open directly
      fetchAndShow(c.id);
    } else {
      setSelectedId(c.id);
      setSelectedName(c.full_name);
    }
  };

  const fetchAndShow = async (id) => {
    const { data } = await api.get(`/cards/${id}`);
    setSelectedCard(data);
  };

  const acknowledge = () => {
    const next = new Set(acked);
    next.add(selectedId);
    setAcked(next); saveAckSet(next);
    fetchAndShow(selectedId);
    setSelectedId(null);
  };

  return (
    <div className="min-h-screen flex flex-col paper-bg" data-testid="field-agent-home">
      <IdleLogout idleSeconds={480} warnAtRemaining={60} />
      <Header />
      <PageHeaderBand
        icon={ShieldCheck}
        title={`Welcome, ${user?.name || "agent"}`}
        subtitle="Read-only view of clients assigned to you. Every record contains PHI/HIPAA-protected data — handle with care."
        testId="field-agent-banner"
      />

      <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-4xl w-full mx-auto space-y-4 flex-1">
        <div className="bg-[#FFF9C4] border border-[#FBBF24] rounded-md p-3 text-xs text-[#15233B] flex items-start gap-2" data-testid="phi-reminder-banner">
          <ShieldCheck className="h-4 w-4 text-[#b91c1c] shrink-0 mt-0.5" />
          <div>
            <b>Privacy reminder:</b> never use public Wi-Fi, always use a privacy screen, and lock your device when stepping away. Tapping any client below will require a HIPAA acknowledgment. <b>You will be signed out automatically after 8 minutes of inactivity.</b>
          </div>
        </div>

        {cards.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No clients assigned yet"
            hint="When intake assigns you to a client, they'll appear here. If you believe you should see a client, contact your intake coordinator."
            testId="field-agent-empty"
          />
        ) : (
          <ul className="bg-white border border-[#15233B]/10 rounded-lg shadow-sm divide-y divide-[#15233B]/8 overflow-hidden">
            {cards.map((c) => {
              const sc = stageColor(c.current_stage);
              return (
                <li key={c.id}>
                  <button
                    onClick={() => askToOpen(c)}
                    data-testid={`fa-client-${c.av_id}`}
                    className="w-full px-5 py-3 flex items-center gap-3 hover:bg-[#EEF2F8] text-left"
                  >
                    <div
                      className="h-10 w-10 rounded-full grid place-items-center text-xs font-bold shadow-sm"
                      style={{ backgroundColor: sc.primary, color: sc.text }}
                    >
                      {deriveInitials(c.full_name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-[#15233B] truncate">
                        {c.full_name} <span className="font-mono text-xs text-[#15233B]/55">{c.av_id}</span>
                      </div>
                      <div className="text-xs text-[#15233B]/60 truncate flex items-center gap-3 mt-0.5">
                        <span className="inline-flex items-center gap-1"><Cal className="h-3 w-3" /> Stage {c.current_stage}: {tplByStage[c.current_stage]?.name}</span>
                        {c.county && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {c.county}</span>}
                        {c.payer_mco && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {c.payer_mco}</span>}
                      </div>
                    </div>
                    {acked.has(c.id) ? (
                      <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#2E7D32]">Acknowledged</span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#b91c1c]">PHI · ack needed</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {selectedId && (
        <PhiWarningModal
          clientName={selectedName}
          onAcknowledge={acknowledge}
          onCancel={() => setSelectedId(null)}
        />
      )}

      {selectedCard && (
        <FieldAgentClientView card={selectedCard} template={tplByStage[selectedCard.current_stage]} onClose={() => setSelectedCard(null)} />
      )}

      <BrandFooter />
    </div>
  );
}

function FieldAgentClientView({ card, template, onClose }) {
  const sc = stageColor(card.current_stage);
  return (
    <div className="fixed inset-0 z-[60] bg-[#15233B]/40 backdrop-blur-sm flex items-stretch" data-testid="fa-client-view">
      <div className="ml-auto bg-white w-full max-w-xl h-full overflow-y-auto shadow-2xl">
        <div className="px-6 py-4 bg-[#15233B] text-white flex items-center gap-3">
          <div
            className="h-12 w-12 rounded-lg grid place-items-center text-base font-mono font-extrabold shadow-sm"
            style={{ backgroundColor: sc.primary, color: sc.text }}
          >
            {String(card.current_stage).padStart(2, "0")}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#FFCB05]">
              Stage {card.current_stage} of 16 · {template?.name}
            </div>
            <h3 className="text-lg font-extrabold tracking-tight">{card.full_name}</h3>
            <div className="text-xs text-white/70 mt-0.5">
              <span className="font-mono">{card.av_id}</span> · {card.payer_mco}
              {card.county && <> · {card.county}</>}
            </div>
          </div>
          <button onClick={onClose} data-testid="fa-close" className="px-3 py-1.5 rounded bg-white/10 hover:bg-white/20 text-xs font-bold">Close</button>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-[#b91c1c]/5 border border-[#b91c1c]/20 rounded-md p-3 text-xs text-[#b91c1c] font-semibold">
            Read-only view · PHI — do not screenshot or share.
          </div>

          <section>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/55 mb-1">Stage tasks</div>
            <ul className="space-y-1">
              {(card.tasks_state || []).map((t, i) => (
                <li key={i} className="flex items-center gap-2 px-3 py-2 rounded bg-[#F2F2F2] text-sm">
                  <span className={`h-4 w-4 rounded-full border-2 ${t.done ? "bg-[#2E7D32] border-[#2E7D32]" : "border-[#15233B]/30"}`} />
                  <span className={t.done ? "line-through text-[#15233B]/55" : "text-[#15233B] font-semibold"}>{t.title}</span>
                </li>
              ))}
            </ul>
          </section>

          {card.next_action_date && (
            <section>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/55 mb-1">Next action</div>
              <div className="text-sm font-bold text-[#15233B]">{card.next_action_date}</div>
              <div className="text-sm text-[#15233B]/70">{card.next_action_text || "—"}</div>
            </section>
          )}

          {card.blocked && (
            <section className="bg-[#FFE5B4] border border-[#FFCC80] rounded-md p-3">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#E65100]">Blocked</div>
              <div className="text-sm text-[#15233B] mt-1">{card.blocked_reason}</div>
            </section>
          )}

          {card.note && (
            <section>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/55 mb-1">Notes</div>
              <div className="text-sm text-[#15233B] whitespace-pre-wrap bg-[#F2F2F2] rounded p-3">{card.note}</div>
            </section>
          )}

          <section className="pt-3 border-t border-[#15233B]/10 text-[10px] text-[#15233B]/50">
            Read-only — to update this record, contact the intake team.
          </section>
        </div>
      </div>
    </div>
  );
}
